import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api-crm.wh.tec.br/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authService = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (name: string, email: string, password: string, role?: string) => {
    const response = await api.post('/auth/register', { name, email, password, role });
    return response.data;
  },
};

export const consultoresService = {
  getConsultores: async (filters: any = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    
    const response = await api.get(`/consultores?${params}`);
    return response.data;
  },

  getConsultor: async (id: number) => {
    const response = await api.get(`/consultores/${id}`);
    return response.data;
  },

  createConsultor: async (consultor: any) => {
    const response = await api.post('/consultores', consultor);
    return response.data;
  },

  updateConsultor: async (id: number, consultor: any) => {
    const response = await api.put(`/consultores/${id}`, consultor);
    return response.data;
  },

  updatePassword: async (id: number, passwords: any) => {
    const response = await api.patch(`/consultores/${id}/password`, passwords);
    return response.data;
  },

  deleteConsultor: async (id: number) => {
    const response = await api.delete(`/consultores/${id}`);
    return response.data;
  },

  transferLeads: async (transfer: any) => {
    const response = await api.post('/consultores/transfer-leads', transfer);
    return response.data;
  },

  getConsultorStats: async (id: number, period: string = '30d') => {
    const response = await api.get(`/consultores/${id}/stats?period=${period}`);
    return response.data;
  }
};

export const leadsService = {
  getLeads: async (params?: { consultor?: number; status?: string; telefone?: string; page?: number; limit?: number }) => {
    const response = await api.get('/leads', { params });
    return response.data;
  },
  createLead: async (lead: any) => {
    const response = await api.post('/leads', lead);
    return response.data;
  },
  updateLead: async (id: number, lead: any) => {
    const response = await api.put(`/leads/${id}`, lead);
    return response.data;
  },
  updateLeadStatus: async (id: number, status_lead: string) => {
    const response = await api.patch(`/leads/${id}/status`, { status_lead });
    return response.data;
  },
  updateLeadComprou: async (id: number, comprou: boolean, data_venda?: string) => {
    const response = await api.patch(`/leads/${id}/comprou`, { comprou, data_venda });
    return response.data;
  },
  deleteLead: async (id: number) => {
    const response = await api.delete(`/leads/${id}`);
    return response.data;
  },
  // Analytics endpoints
  getConsultorMetrics: async (params?: { period?: string; consultor_id?: string }) => {
    const response = await api.get('/leads/analytics/consultors', { params });
    return response.data;
  },
  getFunnelData: async (params?: { period?: string }) => {
    const response = await api.get('/leads/analytics/funnel', { params });
    return response.data;
  },
  getTrendData: async (params?: { period?: string }) => {
    const response = await api.get('/leads/analytics/trends', { params });
    return response.data;
  },
  getLeadStats: async (params?: { period?: string }) => {
    const response = await api.get('/leads/analytics/stats', { params });
    return response.data;
  },
};

export const dashboardService = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
};

export const whatsappService = {
  sendMessage: async (data: { leadId: number; phone: string; message: string; templateId?: number }) => {
    const response = await api.post('/whatsapp/send', data);
    return response.data;
  },
  getMessageHistory: async (leadId: number) => {
    const response = await api.get(`/whatsapp/messages/${leadId}`);
    return response.data;
  },
  getTemplates: async () => {
    const response = await api.get('/whatsapp/templates');
    return response.data;
  },
  getTemplatesByStatus: async (status: string) => {
    const response = await api.get(`/whatsapp/templates/status/${status}`);
    return response.data;
  },
  createTemplate: async (template: { name: string; content: string; status_lead?: string }) => {
    const response = await api.post('/whatsapp/templates', template);
    return response.data;
  },
  updateTemplate: async (id: number, template: any) => {
    const response = await api.put(`/whatsapp/templates/${id}`, template);
    return response.data;
  },
  deleteTemplate: async (id: number) => {
    const response = await api.delete(`/whatsapp/templates/${id}`);
    return response.data;
  },
  getInstanceStatus: async () => {
    const response = await api.get('/whatsapp/status');
    return response.data;
  },
};

export const analyticsService = {
  getDashboard: async (params?: { userId?: number; dateFrom?: string; dateTo?: string }) => {
    const response = await api.get('/analytics/dashboard', { params });
    return response.data;
  },
  getDailyStats: async (userId?: number) => {
    const response = await api.get('/analytics/daily', { params: { userId } });
    return response.data;
  },
  getMessageStatusStats: async (params?: { userId?: number; dateFrom?: string; dateTo?: string }) => {
    const response = await api.get('/analytics/message-status', { params });
    return response.data;
  },
  getUserStats: async (params?: { dateFrom?: string; dateTo?: string }) => {
    const response = await api.get('/analytics/users', { params });
    return response.data;
  },
  getTopTemplates: async (params?: { userId?: number; dateFrom?: string; dateTo?: string }) => {
    const response = await api.get('/analytics/templates', { params });
    return response.data;
  },
  getAutomationRules: async () => {
    const response = await api.get('/analytics/automation/rules');
    return response.data;
  },
  createAutomationRule: async (rule: {
    name: string;
    description: string;
    trigger_event: string;
    trigger_status: string;
    trigger_delay_minutes: number;
    target_template_id: number;
  }) => {
    const response = await api.post('/analytics/automation/rules', rule);
    return response.data;
  },
};

export default api;